import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../utils/apiAdmin';
import { useAdminWebSocket } from '../../hooks/useProjectWebSocket';
import { useAuditTrail, MAX_EVENTS, SYSTEM_SCOPE } from './hooks/useAuditTrail';
import AuditFilters from '../../components/AuditFilters';
import AuditTable, { isAccessRow } from '../../components/AuditTable';
import AuditEntityPanel from '../../components/AuditEntityPanel';

/**
 * The admin audit page.
 *
 * The trail has captured before/after diffs since day one and nothing in the
 * portal ever read it. This is that read: every recorded event, in words,
 * filterable, searchable, and exportable when the trail is evidence.
 *
 * ⚠️ NO RESOLUTION LOGIC LIVES HERE. Names, subjects and value labels arrive
 * resolved from GET /admin/audit, which shares its resolver with the MCP history
 * tools so the two cannot drift apart.
 */

const FILTER_STORAGE_KEY = 'adminAuditFilters';
const PANEL_STORAGE_KEY = 'adminAuditPanel';

/**
 * Which events belong to the open slide-over.
 *
 * ⚠️ DERIVED FROM THE SUBJECT, NEVER STORED ON IT. The subject used to carry a
 * `match` FUNCTION, which cannot be written to localStorage - JSON.stringify
 * drops it silently, so the panel would have restored with no way to find its
 * own events and rendered empty. Keeping the rule here makes the subject plain
 * data: `{ kind, key, title, subtitle }`.
 */
export function matchFor(subject) {
    if (!subject) return () => false;
    return subject.kind === 'actor'
        ? event => (event.actorId || event.actorLogin) === subject.key
        : event => event.entityId === subject.key;
}

/** Restore a persisted value, treating anything unparseable as absent. */
export function readStored(key, fallback) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
}

const DEFAULT_FILTERS = {
    scope: SYSTEM_SCOPE,
    preset: '7d',
    startDate: '',
    endDate: '',
    rowType: 'all',
    action: '',
    entityType: '',
    actor: '',
    via: '',
    field: '',
    search: '',
    hideNoOp: false
};

const isoDaysAgo = days => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().slice(0, 10);
};

/** The server-side window the current preset means. */
export function windowFor({ preset, startDate, endDate }) {
    if (preset === 'custom') return { startDate, endDate };
    const days = preset === '24h' ? 1 : preset === '30d' ? 30 : 7;
    return { startDate: isoDaysAgo(days), endDate: '' };
}

// ---------------------------------------------------------------------------
// Client-side narrowing, over the window already loaded
// ---------------------------------------------------------------------------

/** Every string in an event that a reader might search for. */
export function searchableText(event) {
    return [
        event.action,
        event.actorName, event.actorLogin, event.actorEmail, event.actorId,
        event.entityId, event.entity?.name, event.entity?.parentName,
        event.entityType, event.via, event.source,
        ...(event.fields || []).flatMap(change => [
            change.field,
            change.beforeLabel, change.afterLabel,
            typeof change.before === 'object' ? JSON.stringify(change.before) : change.before,
            typeof change.after === 'object' ? JSON.stringify(change.after) : change.after
        ]),
        JSON.stringify(event.metadata || {})
    ].filter(v => v !== null && v !== undefined).join(' ').toLowerCase();
}

export function matchesSearch(event, term) {
    if (!term) return true;
    return searchableText(event).includes(term.toLowerCase());
}

/**
 * Collapse repeated reports of the SAME delivery event (T438, Bill's option B).
 *
 * Amazon's notification service delivers AT LEAST ONCE, so the same bounce can
 * arrive twice and be recorded twice. Each row carries the event's own identity
 * on `entityId` - `messageId#eventType#recipient` - so the duplicate is
 * recognisable here even though it cannot be prevented at write time: `logAudit`
 * mints a fresh id and timestamp per row, so neither half of the key repeats and
 * no conditional write can see it.
 *
 * ⚠️ SCOPED TO DELIVERY OUTCOMES, DELIBERATELY. Two genuine bounces for the same
 * message and recipient cannot happen - a message is delivered or it is not -
 * but two genuine `task_updated` rows for one task are ordinary, so a blanket
 * "same entity, same action" collapse would HIDE real history. Nothing outside
 * these two actions is touched.
 *
 * The first occurrence is kept, so the recorded time is when we first heard.
 */
const DELIVERY_ACTIONS = new Set(['email_delivery_confirmed', 'email_delivery_failed']);

export function collapseDeliveryDuplicates(events) {
    const seen = new Set();
    return events.filter(event => {
        if (!DELIVERY_ACTIONS.has(event.action)) return true;
        const identity = `${event.action}#${event.entityId ?? ''}`;
        if (seen.has(identity)) return false;
        seen.add(identity);
        return true;
    });
}

export function applyClientFilters(events, filters) {
    return events.filter(event => {
        const access = isAccessRow(event);
        const denied = event.metadata?.outcome === 'denied';

        if (filters.rowType === 'change' && access) return false;
        if (filters.rowType === 'access' && !access) return false;
        if (filters.rowType === 'denied' && !denied) return false;

        if (filters.hideNoOp && event.noOp) return false;
        if (filters.action && event.action !== filters.action) return false;
        if (filters.entityType && event.entityType !== filters.entityType) return false;
        if (filters.via && event.via !== filters.via) return false;

        if (filters.actor) {
            const actor = event.actorName || event.actorLogin || event.actorId || 'unattributed';
            if (actor !== filters.actor) return false;
        }

        if (filters.field && !(event.fields || []).some(c => c.field === filters.field)) return false;

        return matchesSearch(event, filters.search);
    });
}

/**
 * ⚠️ ORIGIN IS A CLOSED SET, UNLIKE EVERY OTHER FILTER HERE. `via` can only ever
 * be one of these four (see viaFor in /opt/nodejs/auditResolve), so absence is a
 * FACT worth stating rather than a value to omit: "no admin connector row in
 * this window" and "this dropdown does not mention the admin connector" are very
 * different statements, and only the first answers "did an agent touch this?".
 *
 * Actions and actors stay derived from the data, because they are open-ended and
 * a list of everything possible would invite selecting one, getting nothing, and
 * concluding it never happened.
 */
export const ALL_ORIGINS = ['admin portal', 'client portal', 'admin connector', 'client connector'];

/** How many events in the window came from each origin. Zero is an answer. */
export function originCounts(events) {
    const counts = Object.fromEntries(ALL_ORIGINS.map(origin => [origin, 0]));
    for (const event of events) {
        if (event.via && counts[event.via] !== undefined) counts[event.via] += 1;
    }
    return counts;
}

/** The dropdown values present in this window - never a list of what is possible. */
export function optionsFrom(events) {
    const actions = new Set();
    const entityTypes = new Set();
    const actors = new Set();
    const origins = new Set();
    const fields = new Set();

    for (const event of events) {
        if (event.action) actions.add(event.action);
        if (event.entityType) entityTypes.add(event.entityType);
        actors.add(event.actorName || event.actorLogin || event.actorId || 'unattributed');
        if (event.via) origins.add(event.via);
        for (const change of event.fields || []) fields.add(change.field);
    }

    const sorted = set => [...set].sort();
    return {
        actions: sorted(actions),
        entityTypes: sorted(entityTypes),
        actors: sorted(actors),
        origins: sorted(origins),
        fields: sorted(fields)
    };
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

/**
 * ⚠️ CSV INJECTION IS HANDLED HERE, AND NOWHERE ELSE CAN DO IT. This export is
 * the one place audit data leaves React's escaping. It carries failed-login
 * usernames - unauthenticated attacker input - plus comment bodies, task
 * subjects, wiki titles and a connector's own tool arguments.
 *
 * A cell whose value begins `=`, `+`, `-`, `@`, tab or carriage return is
 * executed as a FORMULA by Excel and Sheets when the file is opened. Every field
 * is quoted, embedded quotes are doubled, and a dangerous leading character is
 * prefixed with a single quote so the cell is read as text.
 */
const FORMULA_LEAD = ['=', '+', '-', '@', '\t', '\r'];

export function csvCell(value) {
    if (value === null || value === undefined) return '""';
    const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
    const neutered = FORMULA_LEAD.includes(text.charAt(0)) ? `'${text}` : text;
    return `"${neutered.replace(/"/g, '""')}"`;
}

const CSV_COLUMNS = [
    'timestamp', 'type', 'action', 'actorName', 'actorLogin', 'actorId', 'actorType',
    'entityType', 'entityId', 'entityName', 'parentName', 'via', 'source',
    'field', 'before', 'after', 'beforeLabel', 'afterLabel', 'noOp', 'outcome', 'auditId'
];

/** One row per CHANGED FIELD, so a diff is readable in a spreadsheet. */
export function toCsv(events) {
    const lines = [CSV_COLUMNS.join(',')];

    for (const event of events) {
        const base = {
            timestamp: event.timestamp,
            type: isAccessRow(event) ? 'access' : 'change',
            action: event.action,
            actorName: event.actorName,
            actorLogin: event.actorLogin,
            actorId: event.actorId,
            actorType: event.actorType,
            entityType: event.entityType,
            entityId: event.entityId,
            entityName: event.entity?.name,
            parentName: event.entity?.parentName,
            via: event.via,
            source: event.source,
            outcome: event.metadata?.outcome,
            auditId: event.auditId
        };

        const changes = event.fields?.length ? event.fields : [null];
        for (const change of changes) {
            const row = {
                ...base,
                field: change?.field,
                before: change?.before,
                after: change?.after,
                beforeLabel: change?.beforeLabel,
                afterLabel: change?.afterLabel,
                noOp: change ? String(change.noOp) : ''
            };
            lines.push(CSV_COLUMNS.map(column => csvCell(row[column])).join(','));
        }
    }

    return lines.join('\n');
}

// ---------------------------------------------------------------------------

function AuditTrail() {
    const [filters, setFilters] = useState(
        () => ({ ...DEFAULT_FILTERS, ...readStored(FILTER_STORAGE_KEY, {}) })
    );
    const [clients, setClients] = useState([]);
    // The open slide-over survives a reload, like every other toggle in the
    // portal. Plain data only - see matchFor.
    const [subject, setSubject] = useState(() => readStored(PANEL_STORAGE_KEY, null));

    useEffect(() => {
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    }, [filters]);

    useEffect(() => {
        if (subject) localStorage.setItem(PANEL_STORAGE_KEY, JSON.stringify(subject));
        else localStorage.removeItem(PANEL_STORAGE_KEY);
    }, [subject]);

    // Won clients, for the scope picker. Reading this list is itself an audited
    // access event on the server side - as it should be.
    useEffect(() => {
        const load = async () => {
            try {
                const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/leads`);
                if (!response.ok) return;
                const data = await response.json();
                setClients((data.leads || []).filter(lead => lead.status === 'won' && lead.projectId));
            } catch (err) {
                console.error('Failed to load clients for the audit scope picker:', err);
            }
        };
        load();
    }, []);

    const range = useMemo(() => windowFor(filters), [filters]);

    const { events, loading, error, truncated, reload } = useAuditTrail({
        scope: filters.scope,
        include: 'all',
        startDate: range.startDate,
        endDate: range.endDate
    });

    const options = useMemo(() => optionsFrom(events), [events]);
    const origins = useMemo(() => originCounts(events), [events]);
    const visible = useMemo(
        () => applyClientFilters(collapseDeliveryDuplicates(events), filters),
        [events, filters]
    );

    // ⚠️ A COUNTER, NOT AN INSERTION. New events announce themselves and wait to
    // be asked for: the list must not move under a reader who is part-way
    // through it, because this page is read as evidence. Clicking the pill is
    // what loads them.
    const [pending, setPending] = useState(0);
    useEffect(() => { setPending(0); }, [events]);

    const onAuditMessage = useCallback(message => {
        if (message?.type !== 'audit_updated') return;
        // Only the partition being shown. Access rows never stream at all, so
        // this page's own reads cannot wake it.
        if (message.projectId !== filters.scope) return;
        setPending(count => count + 1);
    }, [filters.scope]);

    useAdminWebSocket(onAuditMessage);

    const windowLabel = range.startDate
        ? `${range.startDate} → ${range.endDate || 'now'}`
        : 'the whole trail';

    const download = useCallback(() => {
        const blob = new Blob([toCsv(visible)], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `audit-${filters.scope}-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }, [visible, filters.scope]);

    const selectEntity = useCallback(event => {
        const key = event.entityId;
        setSubject({
            kind: 'entity',
            key,
            title: event.entity?.name || key || 'Unnamed entity',
            subtitle: key
        });
    }, []);

    const selectActor = useCallback(event => {
        const key = event.actorId || event.actorLogin;
        setSubject({
            kind: 'actor',
            key,
            title: event.actorName || event.actorLogin || key || 'Unattributed',
            subtitle: event.actorEmail || key
        });
    }, []);

    const subjectEvents = useMemo(
        () => (subject ? events.filter(matchFor(subject)) : []),
        [subject, events]
    );

    return (
        <div className="flex flex-col h-full bg-gray-50">
            {/* No page title here - the tab in AdminLayout already names it. */}
            <div className="px-4 pt-4">
                <p className="text-sm text-gray-500">
                    Every recorded change, access and sign-in event. Reading this page is
                    itself recorded.
                </p>
            </div>

            <AuditFilters
                filters={filters}
                setFilters={setFilters}
                clients={clients}
                options={options}
                origins={origins}
                disabled={loading}
            />

            {/* ⚠️ THE BOUND IS ALWAYS STATED. Free text searches the loaded
                window, so a reader who cannot see what was loaded cannot tell
                "no match" from "not in this window". */}
            <div className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm text-gray-600 bg-white border-b border-gray-200">
                {loading ? (
                    <span>Loading the window…</span>
                ) : (
                    <span>
                        Searched <strong>{events.length}</strong> events, {windowLabel}.
                        Showing <strong>{visible.length}</strong>.
                    </span>
                )}
                {truncated && (
                    <span className="text-amber-700">
                        Capped at {MAX_EVENTS} events — narrow the period to see the rest.
                        Older events in this range were not loaded and are not searched.
                    </span>
                )}
                {error && <span className="text-red-700">{error}</span>}
                {pending > 0 && (
                    <button
                        onClick={reload}
                        className="px-3 py-1 rounded-full bg-primary-700 text-white text-xs hover:bg-primary-800"
                    >
                        {pending} new event{pending === 1 ? '' : 's'} — click to load
                    </button>
                )}
                <div className="ml-auto flex gap-2">
                    <button
                        onClick={reload}
                        className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
                        disabled={loading}
                    >
                        Refresh
                    </button>
                    <button
                        onClick={download}
                        className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
                        disabled={!visible.length}
                    >
                        Export CSV
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-auto">
                <AuditTable
                    events={visible}
                    onSelectEntity={selectEntity}
                    onSelectActor={selectActor}
                />
            </div>

            <AuditEntityPanel
                subject={subject}
                events={subjectEvents}
                windowLabel={windowLabel}
                onClose={() => setSubject(null)}
            />
        </div>
    );
}

export default AuditTrail;
